/**
 * Template Instructions helpers (CRE report page).
 * - Predefined texts the user can insert with one click (e.g. Resident / מתמחה).
 * - Rule for what happens to the field when another template is selected.
 */

/** Placeholder the doctor replaces with the resident's report (highlighted in the UI). */
export const RESIDENT_PASTE_PLACEHOLDER = "[הדבק כאן את תשובת המתמחה]";

export const RESIDENT_PRESET_TEXT = [
  "התשובה המודבקת מטה היא טיוטת המתמחה לבדיקה הנוכחית.",
  "",
  "יש לעדכן אותה לפי דיקטציית הרופא המומחה ובהתאם למבנה התבנית של המומחה.",
  "",
  "דיקטציית המומחה גוברת במקרה של סתירה. יש לשמור ממצאים קיימים שלא תוקנו או הוסרו, ולעדכן את הסיכום בהתאם.",
  "",
  "אין להתייחס לתשובת המתמחה כבדיקה קודמת.",
  "",
  "תשובת המתמחה:",
  RESIDENT_PASTE_PLACEHOLDER,
].join("\n");

/** Placeholders highlighted in the Template Instructions field. */
export const TEMPLATE_INSTRUCTIONS_HIGHLIGHTS = [RESIDENT_PASTE_PLACEHOLDER];

/**
 * Insert a preset at the caret / selection, separated from surrounding text by a
 * blank line. Returns the new value and where the preset's placeholder landed so
 * the UI can select it (paste then replaces it).
 *
 * @param {string} value - current field value
 * @param {number|null|undefined} selStart
 * @param {number|null|undefined} selEnd
 * @param {string} preset
 * @param {string} [placeholder]
 * @returns {{ value: string, placeholderStart: number, placeholderEnd: number }}
 */
export function insertPresetText(
  value,
  selStart,
  selEnd,
  preset,
  placeholder = RESIDENT_PASTE_PLACEHOLDER,
) {
  const current = String(value ?? "");
  const len = current.length;
  const start = Number.isFinite(selStart) ? Math.min(Math.max(selStart, 0), len) : len;
  const end = Number.isFinite(selEnd) ? Math.min(Math.max(selEnd, start), len) : start;

  const before = current.slice(0, start);
  const after = current.slice(end);

  const gapBefore =
    before.length === 0 || before.endsWith("\n\n")
      ? ""
      : before.endsWith("\n")
        ? "\n"
        : "\n\n";
  const gapAfter =
    after.length === 0 || after.startsWith("\n\n")
      ? ""
      : after.startsWith("\n")
        ? "\n"
        : "\n\n";

  const insertedAt = before.length + gapBefore.length;
  const next = before + gapBefore + preset + gapAfter + after;
  const idx = placeholder ? preset.lastIndexOf(placeholder) : -1;
  const placeholderStart = idx >= 0 ? insertedAt + idx : insertedAt + preset.length;
  const placeholderEnd = idx >= 0 ? placeholderStart + placeholder.length : placeholderStart;

  return { value: next, placeholderStart, placeholderEnd };
}

/** First line of the Resident preset, and its report heading (fallback if the intro was edited). */
const RESIDENT_BLOCK_MARKERS = [
  RESIDENT_PRESET_TEXT.split("\n")[0],
  "תשובת המתמחה:",
];

/**
 * The Resident block (predefined text + pasted resident report) at the end of
 * the field, or "" if the field has none. The Resident button always appends,
 * so the block runs from its first line to the end of the field.
 * @param {string} text
 * @returns {string}
 */
export function extractResidentBlock(text) {
  const source = String(text ?? "");
  for (const marker of RESIDENT_BLOCK_MARKERS) {
    const idx = source.indexOf(marker);
    if (idx >= 0) return source.slice(idx).trim();
  }
  return "";
}

/**
 * Field value after the user selects another template (agreed with Jacky):
 *   new template's saved instructions + manually entered text + Resident block.
 * Only the previous template's saved instructions are swapped out. If the user
 * edited them, they no longer match and are kept as manual text.
 *
 * @param {string} current - field value now
 * @param {string} previousTemplate - instructions the previous template loaded
 * @param {string} incoming - the newly selected template's saved instructions
 * @returns {string}
 */
export function resolveTemplateInstructionsOnSelect(
  current,
  previousTemplate,
  incoming,
) {
  const source = String(current ?? "");
  const residentPart = extractResidentBlock(source);
  let manualPart = residentPart
    ? source.slice(0, source.lastIndexOf(residentPart))
    : source;

  const previous = String(previousTemplate ?? "").trim();
  if (previous && manualPart.includes(previous)) {
    manualPart = manualPart.replace(previous, "");
  }
  manualPart = manualPart.replace(/\n{3,}/g, "\n\n").trim();

  const templatePart = String(incoming ?? "").trim();
  return [templatePart, manualPart, residentPart].filter(Boolean).join("\n\n");
}

/**
 * Split text into plain / highlighted segments for the highlight layer.
 * @param {string} text
 * @param {string[]} [highlights]
 * @returns {Array<{ text: string, highlight: boolean }>}
 */
export function splitHighlightSegments(text, highlights = TEMPLATE_INSTRUCTIONS_HIGHLIGHTS) {
  const source = String(text ?? "");
  const tokens = (highlights || []).filter(Boolean);
  if (!source || tokens.length === 0) return [{ text: source, highlight: false }];
  const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "g");
  return source
    .split(re)
    .filter((part) => part !== "")
    .map((part) => ({ text: part, highlight: tokens.includes(part) }));
}
